// Particle system management
export {
    createParticleSystem,
    updateParticles,
    drawParticles,
    createThemeParticles,
    generateAttackParticles
  };
  
  /**
   * Creates a new particle system with the specified options
   * @param {Object} options - Configuration options for the particle system
   * @returns {Object} - Configured particle system
   */
  function createParticleSystem(options) {
    // Create a new particle system
    return {
      particles: [],
      maxParticles: options.maxParticles || 50,
      emitRate: options.emitRate || 5,
      position: options.position || { x: 0, y: 0 },
      colors: options.colors || ['#ffffff'],
      size: options.size || { min: 2, max: 5 },
      speed: options.speed || { min: 1, max: 3 },
      lifetime: options.lifetime || { min: 500, max: 1500 },
      direction: options.direction || { minAngle: 0, maxAngle: Math.PI * 2 },
      gravity: options.gravity || 0,
      lastEmit: 0
    };
  }
  
  /**
   * Updates particle positions, lifetimes, etc. for a particle system
   * @param {Object} particleSystem - The particle system to update
   * @param {number} deltaTime - Time since last frame in milliseconds
   */
  function updateParticles(particleSystem, deltaTime) {
    // Update existing particles
    for (let i = particleSystem.particles.length - 1; i >= 0; i--) {
      const particle = particleSystem.particles[i];
      
      // Update particle position
      particle.x += particle.velocityX * deltaTime / 16;
      particle.y += particle.velocityY * deltaTime / 16;
      
      // Apply gravity if specified
      if (particleSystem.gravity) {
        particle.velocityY += particleSystem.gravity * deltaTime / 16;
      }
      
      // Update lifetime
      particle.lifetime -= deltaTime;
      
      // Remove expired particles
      if (particle.lifetime <= 0) {
        particleSystem.particles.splice(i, 1);
      }
    }
    
    // Emit new particles based on rate
    const now = performance.now();
    if (now - particleSystem.lastEmit > 1000 / particleSystem.emitRate) {
      if (particleSystem.particles.length < particleSystem.maxParticles) {
        // Create new particle
        const particle = createParticle(particleSystem);
        particleSystem.particles.push(particle);
      }
      particleSystem.lastEmit = now;
    }
  }
  
  /**
   * Creates a single particle for the specified particle system
   * @param {Object} system - The particle system
   * @returns {Object} - A new particle
   * @private
   */
  function createParticle(system) {
    // Random angle based on direction range
    const angle = system.direction.minAngle + 
                  Math.random() * (system.direction.maxAngle - system.direction.minAngle);
    
    // Random speed based on range
    const speed = system.speed.min + Math.random() * (system.speed.max - system.speed.min);
    
    // Create particle
    return {
      x: system.position.x,
      y: system.position.y,
      velocityX: Math.cos(angle) * speed,
      velocityY: Math.sin(angle) * speed,
      size: system.size.min + Math.random() * (system.size.max - system.size.min),
      color: system.colors[Math.floor(Math.random() * system.colors.length)],
      lifetime: system.lifetime.min + Math.random() * (system.lifetime.max - system.lifetime.min)
    };
  }
  
  /**
   * Draws all particles in a particle system
   * @param {CanvasRenderingContext2D} ctx - Canvas rendering context
   * @param {Object} particleSystem - The particle system to draw
   */
  function drawParticles(ctx, particleSystem) {
    // Draw all particles in the system
    particleSystem.particles.forEach(particle => {
      // Save current context state
      ctx.save();
      
      // Set color and alpha based on lifetime
      ctx.fillStyle = particle.color;
      ctx.globalAlpha = Math.min(1, particle.lifetime / 300);
      
      // Draw the particle
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
      ctx.fill();
      
      // Restore context state
      ctx.restore();
    });
  }
  
  /**
   * Creates theme-specific particles
   * @param {Object} theme - The current battle theme
   * @param {number} width - Canvas width
   * @param {number} height - Canvas height
   */
  function createThemeParticles(theme, width, height) {
    const numParticles = 20;
    theme.particles = [];
    
    // Color palettes for different themes
    const themeColors = {
      volcano: ['#ff6b00', '#ff4500', '#ff8c00'],
      ice: ['#a5f2f3', '#ffffff', '#d1f3ff'],
      jungle: ['#7cff00', '#00aa11', '#90ee90'],
      space: ['#ffffff', '#aaaaff', '#ffaaff'],
      candy: ['#ff69b4', '#ff1493', '#00ffff'],
      underwater: ['#00aaff', '#0088cc', '#00ddff']
    };
    
    // Create particles based on theme
    for (let i = 0; i < numParticles; i++) {
      let particle = {
        x: Math.random() * width,
        y: Math.random() * height,
        velocityX: (Math.random() - 0.5) * 2,
        velocityY: (Math.random() - 0.5) * 2,
        size: 2 + Math.random() * 3,
        color: themeColors[theme.name][Math.floor(Math.random() * 3)],
        alpha: 0.5 + Math.random() * 0.5,
        lifetime: 5000 + Math.random() * 5000
      };
      
      // Theme-specific particle properties
      switch (theme.name) {
        case 'space':
          // Stars twinkle
          particle.twinkleSpeed = 0.05 + Math.random() * 0.05;
          particle.twinkleOffset = Math.random() * Math.PI * 2;
          break;
        case 'underwater':
          // Bubbles rise
          particle.velocityY = -0.5 - Math.random();
          particle.velocityX = (Math.random() - 0.5) * 0.5;
          particle.size = 3 + Math.random() * 8;
          break;
        case 'volcano':
          // Embers rise
          particle.velocityY = -1 - Math.random() * 2;
          particle.y = height - Math.random() * 100;
          break;
      }
      
      theme.particles.push(particle);
    }
  }
  
  /**
   * Generates particles for attack effects
   * @param {Object} attack - The attack object
   * @returns {Array} - Array of particle objects
   */
  function generateAttackParticles(attack) {
    const PARTICLE_COUNT = 15;
    const particles = [];
    
    // Generate particles based on attack properties
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 1 + Math.random() * 3;
      
      particles.push({
        x: attack.x + attack.width / 2,
        y: attack.y + attack.height / 2,
        velocityX: Math.cos(angle) * speed,
        velocityY: Math.sin(angle) * speed,
        size: 2 + Math.random() * 3,
        color: attack.color,
        lifetime: 300 + Math.random() * 500
      });
    }
    
    return particles;
  }
  
  // Add other particle utility functions here